import { useEffect, useState } from 'react'
import { alsDauer, alsUhrzeit, api } from '../../api/client'
import type { Befund } from './typen'

type Art = 'FAHRER' | 'FAHRZEUG'

interface Posten {
  art: string
  beschreibung: string
  punkte: number
  minuten: number | null
}

interface Auswirkung {
  punkte: number
  posten: Posten[]
}

interface Kandidat {
  id: number
  bezeichnung: string
  zusatz: string
  moeglich: boolean
  befunde: Befund[]
  auswirkung: Auswirkung
}

interface Luecke {
  betriebstag: string
  wochentag: string
  kursId: number
  kursnummer: string
  dienstbeginn: number | null
  dienstende: number | null
  arbeitszeit: number | null
  zuteilungId: number | null
  bisher: string
  loesbar: boolean
  kandidaten: Kandidat[]
}

interface Ergebnis {
  art: Art
  id: number
  bezeichnung: string
  von: string
  bis: string
  betroffeneKurse: number
  loesbareLuecken: number
  restluecken: number
  luecken: Luecke[]
}

interface Fahrer {
  id: number
  vorname: string
  nachname: string
  personalnummer: string
  aktiv: boolean
}

interface Fahrzeug {
  id: number
  kennzeichen: string
  interneNummer: string | null
  aktiv: boolean
}

function heute(): string {
  return new Date().toISOString().slice(0, 10)
}

/** Je weniger Punkte, desto unauffälliger die Darstellung. */
function ampel(punkte: number): string {
  if (punkte === 0) return 'erfolg'
  if (punkte <= 10) return 'hinweis'
  return 'fehler'
}

/**
 * Ausfallhilfe: „Meier fällt aus — was jetzt?"
 *
 * Zeigt alle betroffenen Kurse, je Kurs die möglichen Ersatzbesetzungen mit ihrem Preis,
 * und hebt die Lücken hervor, für die es keine zulässige Lösung gibt. Diese Restlücken sind
 * das eigentliche Ergebnis — sie verlangen eine Entscheidung, die kein Programm treffen darf.
 */
export function Ausfallhilfe() {
  const [art, setArt] = useState<Art>('FAHRER')
  const [id, setId] = useState<string>('')
  const [von, setVon] = useState(heute())
  const [bis, setBis] = useState(heute())

  const [fahrer, setFahrer] = useState<Fahrer[]>([])
  const [fahrzeuge, setFahrzeuge] = useState<Fahrzeug[]>([])
  const [ergebnis, setErgebnis] = useState<Ergebnis | null>(null)
  const [offen, setOffen] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)

  useEffect(() => {
    void (async () => {
      try {
        const [p, f] = await Promise.all([
          api.get<Fahrer[]>('/api/fahrpersonal'),
          api.get<Fahrzeug[]>('/api/stammdaten/fahrzeuge'),
        ])
        setFahrer(p.filter((x) => x.aktiv))
        setFahrzeuge(f.filter((x) => x.aktiv))
      } catch (ex) {
        setFehler(ex instanceof Error ? ex.message : 'Stammdaten konnten nicht geladen werden')
      }
    })()
  }, [])

  function artWechseln(neu: Art) {
    setArt(neu)
    setId('')
    setErgebnis(null)
  }

  async function analysieren() {
    if (!id) return
    setLaeuft(true)
    setFehler(null)
    setErgebnis(null)
    try {
      const antwort = await api.get<Ergebnis>(
        `/api/disposition/ausfall?art=${art}&id=${id}&von=${von}&bis=${bis}`,
      )
      setErgebnis(antwort)
      // Die erste ungelöste Lücke aufklappen — dorthin muss der Blick zuerst.
      const kritisch = antwort.luecken.find((l) => !l.loesbar) ?? antwort.luecken[0]
      setOffen(kritisch ? schluessel(kritisch) : null)
    } catch (ex) {
      setFehler(ex instanceof Error ? ex.message : 'Analyse fehlgeschlagen')
    } finally {
      setLaeuft(false)
    }
  }

  function schluessel(l: Luecke): string {
    return `${l.betriebstag}|${l.kursnummer}`
  }

  async function besetzen(luecke: Luecke, kandidat: Kandidat) {
    setFehler(null)
    try {
      await api.post('/api/disposition/besetzen', {
        betriebstag: luecke.betriebstag,
        kursId: luecke.kursId,
        fahrerId: ergebnis?.art === 'FAHRER' ? kandidat.id : null,
        fahrzeugId: ergebnis?.art === 'FAHRZEUG' ? kandidat.id : null,
        status: 'GEPLANT',
        bemerkung: `Ersatz für ${ergebnis?.bezeichnung}`,
        trotzdem: false,
      })
      await analysieren()
    } catch (ex) {
      setFehler(ex instanceof Error ? ex.message : 'Besetzen fehlgeschlagen')
    }
  }

  const auswahl = art === 'FAHRER' ? fahrer : fahrzeuge

  return (
    <>
      <div className="inhalt-kopf">
        <div>
          <h1>Ausfallhilfe</h1>
          <p>
            Wer oder was fällt aus — und was bedeutet das? Zeigt die betroffenen Kurse, mögliche
            Ersatzbesetzungen mit ihrem Preis und die Lücken, für die es keine Lösung gibt.
          </p>
        </div>
      </div>

      {fehler && <div className="meldung fehler">{fehler}</div>}

      <div className="karte abschnitt">
        <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
          <button
            className={art === 'FAHRER' ? 'knopf-primaer' : 'knopf-schlicht'}
            onClick={() => artWechseln('FAHRER')}
          >
            Fahrer fällt aus
          </button>
          <button
            className={art === 'FAHRZEUG' ? 'knopf-primaer' : 'knopf-schlicht'}
            onClick={() => artWechseln('FAHRZEUG')}
          >
            Fahrzeug fällt aus
          </button>
        </div>

        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <label style={{ flex: '1 1 260px' }}>
            <span style={{ display: 'block', fontSize: '0.82rem', marginBottom: 4 }}>
              {art === 'FAHRER' ? 'Mitarbeiter' : 'Fahrzeug'}
            </span>
            <select className="feld" value={id} onChange={(e) => setId(e.target.value)}>
              <option value="">— bitte wählen —</option>
              {art === 'FAHRER'
                ? fahrer.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.nachname}, {f.vorname} · {f.personalnummer}
                    </option>
                  ))
                : fahrzeuge.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.kennzeichen}
                      {f.interneNummer ? ` · Wagen ${f.interneNummer}` : ''}
                    </option>
                  ))}
            </select>
          </label>
          <label>
            <span style={{ display: 'block', fontSize: '0.82rem', marginBottom: 4 }}>ab</span>
            <input
              className="feld"
              type="date"
              value={von}
              onChange={(e) => {
                setVon(e.target.value)
                if (e.target.value > bis) setBis(e.target.value)
              }}
            />
          </label>
          <label>
            <span style={{ display: 'block', fontSize: '0.82rem', marginBottom: 4 }}>bis</span>
            <input
              className="feld"
              type="date"
              value={bis}
              min={von}
              onChange={(e) => setBis(e.target.value)}
            />
          </label>
          <button
            className="knopf-akzent"
            disabled={!id || laeuft}
            onClick={() => void analysieren()}
          >
            {laeuft ? 'Wird gerechnet …' : 'Auswirkung zeigen'}
          </button>
        </div>

        {auswahl.length === 0 && (
          <p style={{ color: 'var(--grau-500)', fontSize: '0.82rem', marginTop: 12 }}>
            Keine aktiven {art === 'FAHRER' ? 'Mitarbeiter' : 'Fahrzeuge'} vorhanden.
          </p>
        )}
      </div>

      {ergebnis && (
        <>
          <div className="karten-raster">
            <div className="kennzahl">
              <div className="zahl">{ergebnis.betroffeneKurse}</div>
              <div className="etikett">betroffene Kurse</div>
            </div>
            <div className="kennzahl">
              <div className="zahl">{ergebnis.loesbareLuecken}</div>
              <div className="etikett">davon besetzbar</div>
            </div>
            <div className="kennzahl">
              <div className="zahl" style={ergebnis.restluecken > 0 ? { color: '#c0392b' } : {}}>
                {ergebnis.restluecken}
              </div>
              <div className="etikett">ohne zulässige Lösung</div>
            </div>
          </div>

          {ergebnis.betroffeneKurse === 0 && (
            <div className="meldung erfolg">
              {ergebnis.bezeichnung} ist in diesem Zeitraum auf keinem Kurs eingeteilt — der
              Ausfall wirkt sich nicht auf den Plan aus.
            </div>
          )}

          {ergebnis.restluecken > 0 && (
            <div className="meldung fehler">
              Für <strong>{ergebnis.restluecken}</strong> Kurs
              {ergebnis.restluecken === 1 ? '' : 'e'} gibt es keine zulässige Besetzung. Diese
              Lücken brauchen eine Entscheidung: Subunternehmer beauftragen, Fahrt ausfallen
              lassen oder jemanden aus dem Frei anfragen.
            </div>
          )}

          {ergebnis.luecken.map((luecke) => {
            const istOffen = offen === schluessel(luecke)
            const moegliche = luecke.kandidaten.filter((k) => k.moeglich)
            return (
              <div className="karte abschnitt" key={schluessel(luecke)}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    cursor: 'pointer',
                    gap: 12,
                    flexWrap: 'wrap',
                  }}
                  onClick={() => setOffen(istOffen ? null : schluessel(luecke))}
                >
                  <div>
                    <h2 style={{ margin: 0 }}>
                      Kurs {luecke.kursnummer}
                      <span
                        style={{
                          fontWeight: 400,
                          fontSize: '0.88rem',
                          color: 'var(--grau-500)',
                          marginLeft: 10,
                        }}
                      >
                        {luecke.wochentag}, {luecke.betriebstag}
                        {luecke.dienstbeginn != null && (
                          <>
                            {' · '}
                            {alsUhrzeit(luecke.dienstbeginn)} – {alsUhrzeit(luecke.dienstende)}
                            {luecke.arbeitszeit != null && ` · ${alsDauer(luecke.arbeitszeit)}`}
                          </>
                        )}
                      </span>
                    </h2>
                    <p style={{ margin: '4px 0 0', fontSize: '0.84rem', color: 'var(--grau-700)' }}>
                      bisher: {luecke.bisher}
                    </p>
                  </div>
                  <span className={`meldung ${luecke.loesbar ? 'erfolg' : 'fehler'}`}
                        style={{ padding: '4px 12px', fontSize: '0.8rem', margin: 0 }}>
                    {luecke.loesbar
                      ? `${moegliche.length} Ersatz möglich`
                      : 'keine zulässige Besetzung'}
                  </span>
                </div>

                {istOffen && (
                  <div className="tabelle-huelle" style={{ marginTop: 14 }}>
                    <table>
                      <thead>
                        <tr>
                          <th style={{ width: '22%' }}>
                            {ergebnis.art === 'FAHRER' ? 'Mitarbeiter' : 'Fahrzeug'}
                          </th>
                          <th style={{ width: 90 }}>Aufwand</th>
                          <th>Was es kostet</th>
                          <th style={{ width: 110 }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {luecke.kandidaten.map((k) => (
                          <tr key={k.id} style={k.moeglich ? {} : { opacity: 0.62 }}>
                            <td>
                              <strong>{k.bezeichnung}</strong>
                              <div style={{ fontSize: '0.78rem', color: 'var(--grau-500)' }}>
                                {k.zusatz}
                              </div>
                            </td>
                            <td>
                              {k.moeglich ? (
                                <span
                                  className={`meldung ${ampel(k.auswirkung.punkte)}`}
                                  style={{
                                    padding: '2px 10px',
                                    fontSize: '0.76rem',
                                    margin: 0,
                                    display: 'inline-block',
                                  }}
                                >
                                  {k.auswirkung.punkte === 0 ? 'ohne' : k.auswirkung.punkte}
                                </span>
                              ) : (
                                <span style={{ color: '#c0392b', fontSize: '0.78rem' }}>
                                  nicht möglich
                                </span>
                              )}
                            </td>
                            <td style={{ fontSize: '0.82rem', color: 'var(--grau-700)' }}>
                              {k.moeglich ? (
                                k.auswirkung.posten.length === 0 ? (
                                  <span style={{ color: 'var(--grau-500)' }}>
                                    Stammbesetzung, keine Nebenwirkung
                                  </span>
                                ) : (
                                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                                    {k.auswirkung.posten.map((p, i) => (
                                      <li key={i}>{p.beschreibung}</li>
                                    ))}
                                  </ul>
                                )
                              ) : (
                                <ul style={{ margin: 0, paddingLeft: 18 }}>
                                  {k.befunde
                                    .filter((b) => b.schwere === 'SPERRE')
                                    .map((b, i) => (
                                      <li key={i}>{b.meldung}</li>
                                    ))}
                                </ul>
                              )}
                            </td>
                            <td>
                              {k.moeglich && (
                                <button
                                  className="knopf-schlicht"
                                  onClick={() => void besetzen(luecke, k)}
                                >
                                  Einsetzen
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                        {luecke.kandidaten.length === 0 && (
                          <tr>
                            <td colSpan={4} style={{ color: 'var(--grau-500)' }}>
                              Niemand ist für Kurs {luecke.kursnummer} berechtigt. Eine
                              Kursberechtigung lässt sich beim Team oder beim einzelnen
                              Mitarbeiter nachtragen.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )
          })}
        </>
      )}
    </>
  )
}
